import { useState } from 'react';
import * as podcastEpisodeStore from 'store/podcast-episodes';
import Error from 'pages/_error.page';

import { batchUpdates, exhaustive, formatDateTime, imageUrl } from 'shared/util';
import Grid from 'components/Grid';
import { PodcastEpisode, Id } from 'types';
import { Loadable } from 'store/util';
import classNames from 'classnames';
import styles from './index.module.scss';

const MoreButton = ({ onClick }: { onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }) => (
  <div className={styles.loadMoreContainer}>
    <Grid container className={styles.loadMoreButtonContainer}>
      <button onClick={onClick} className={styles.loadMoreButton}>
        Load More
      </button>
    </Grid>
  </div>
);

type EpisodesProps = {
  episodes: PodcastEpisode<'podcastShow'>[];
  slug: string;
  loadMoreEpisodes?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  expanded: boolean;
};
const EpisodesList = ({ episodes, slug, loadMoreEpisodes, expanded }: EpisodesProps) => {
  return (
    <div className={styles.episodesContainer}>
      <div className={styles.episodes}>
        {episodes.map((ep) => (
          <a href={`/podcasts/${slug}/episode/${ep.slug}`} key={ep.id} className={styles.episode}>
            <img
              alt={`${ep.title} Cover Art`}
              src={imageUrl({
                id: ep.coverArt ? ep.coverArt : ep.podcastShow.coverArt,
                width: 46,
                height: 46,
              })}
              height={46}
              width={46}
            />
            <div
              className={classNames(styles.episodeData, expanded ? styles.episodeDataExpanded : '')}
            >
              <h4 className={styles.episodeTitle}>{ep.title}</h4>
              <p className={styles.date}>{formatDateTime(ep.publishedAt, 'mm/dd/yyyy')}</p>
            </div>
          </a>
        ))}
        {loadMoreEpisodes ? (
          <MoreButton onClick={loadMoreEpisodes} />
        ) : (
          <div className={styles.loadMoreContainer}>
            <Grid container className={styles.loadMoreButtonContainer}>
              <button className={styles.loadMoreButton}>Loading...</button>
            </Grid>
          </div>
        )}
      </div>
    </div>
  );
};

const PodcastEpisodeListing = ({
  slug,
  expanded = false,
  excluded = [],
}: {
  slug: string;
  expanded?: boolean;
  excluded?: Array<Id<'PodcastEpisode'>>;
}) => {
  const [offset, setOffset] = useState(0);
  const episodesL = podcastEpisodeStore.getPodcastEpisodes({
    podcastShowSlug: slug,
    offset,
    limit: 5,
  });
  const [previousEpisodes, setPreviousEpisodes] = useState<Array<PodcastEpisode<'podcastShow'>>>(
    [],
  );

  switch (episodesL._tag) {
    case 'NotFound':
      return <Error statusCode={404} />;
    case 'NotLoaded':
      return <EpisodesList episodes={previousEpisodes} slug={slug} expanded={expanded} />;
    case 'Loaded': {
      const newEpisodes = Loadable.getOrElse([], episodesL);
      const allEpisodes = [...previousEpisodes, ...newEpisodes].filter((ep) => {
        return excluded.indexOf(ep.id) < 0;
      });

      const loadMoreEpisodes = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        batchUpdates(() => {
          setPreviousEpisodes((ep) => {
            return [...ep, ...newEpisodes];
          });
          setOffset((prevOffset) => prevOffset + 5);
        });
      };

      return (
        <EpisodesList
          episodes={allEpisodes}
          slug={slug}
          loadMoreEpisodes={loadMoreEpisodes}
          expanded={expanded}
        />
      );
    }

    default:
      return exhaustive(episodesL);
  }
};

export default PodcastEpisodeListing;
